import { motion } from 'framer-motion';
import { Star, TrendingUp, BarChart3, Users } from 'lucide-react';

export function BlogCoverGoogleReviews2026() {
  const stats = [
    { value: '93%', label: 'trust', delay: 0.2 },
    { value: '4.5★', label: 'avg', delay: 0.4 },
    { value: '+47%', label: 'CTR', delay: 0.6 },
  ];

  return (
    <div className="w-full h-full bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-950 flex items-center justify-center overflow-hidden relative">
      {/* Subtle grid */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid2026" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M0 40 L40 40 M40 0 L40 40" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" fill="none" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid2026)" />
        </svg>
      </div>

      {/* Floating stars background */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute"
          style={{
            left: `${10 + (i * 7) % 80}%`,
            top: `${15 + (i * 11) % 70}%`,
          }}
          animate={{
            opacity: [0.1, 0.4, 0.1],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 3 + (i % 3),
            repeat: Infinity,
            delay: i * 0.3,
          }}
        >
          <Star className="w-3 h-3 text-yellow-400/30" fill="currentColor" />
        </motion.div>
      ))}

      {/* Central content */}
      <div className="relative z-10 flex flex-col items-center gap-4">
        {/* Year badge */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="px-4 py-1 rounded-full border border-emerald-400/30 bg-emerald-400/10 text-emerald-300 text-xs font-mono tracking-widest"
        >
          2026
        </motion.div>

        {/* Big star rating visual */}
        <motion.div
          initial={{ opacity: 0, scale: 0.5 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, type: 'spring' }}
          className="flex items-center gap-1"
        >
          {[...Array(5)].map((_, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, rotateY: 90 }}
              animate={{ opacity: 1, rotateY: 0 }}
              transition={{ delay: 0.3 + i * 0.1, duration: 0.4 }}
            >
              <Star
                className={`w-8 h-8 ${i < 4 ? 'text-yellow-400' : 'text-yellow-400/50'}`}
                fill={i < 4 ? 'currentColor' : 'none'}
              />
            </motion.div>
          ))}
        </motion.div>

        {/* Stats row */}
        <div className="flex gap-6 mt-2">
          {stats.map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: stat.delay, duration: 0.5 }}
              className="text-center"
            >
              <div className="text-lg font-bold text-white">{stat.value}</div>
              <div className="text-[10px] text-emerald-300/60 uppercase tracking-wider">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        {/* Trend line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="mt-2"
        >
          <svg width="160" height="40" viewBox="0 0 160 40" className="text-emerald-400">
            <motion.path
              d="M0 35 Q20 30 40 28 Q60 26 80 20 Q100 14 120 10 Q140 6 160 2"
              stroke="currentColor"
              strokeWidth="2"
              fill="none"
              initial={{ pathLength: 0 }}
              animate={{ pathLength: 1 }}
              transition={{ delay: 1, duration: 1.5, ease: 'easeOut' }}
            />
            <motion.circle
              cx="160"
              cy="2"
              r="3"
              fill="currentColor"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2.5 }}
            />
          </svg>
        </motion.div>
      </div>

      {/* Corner icons */}
      <motion.div
        className="absolute top-4 right-4 text-emerald-400/20"
        animate={{ rotate: [0, 10, 0] }}
        transition={{ duration: 4, repeat: Infinity }}
      >
        <TrendingUp className="w-8 h-8" />
      </motion.div>
      <motion.div
        className="absolute bottom-4 left-4 text-emerald-400/20"
        animate={{ rotate: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity }}
      >
        <BarChart3 className="w-8 h-8" />
      </motion.div>
    </div>
  );
}
